import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';

import { AggregationTimeScale, CsvAsStringArrays } from '@malou-io/package-utils';

import { AbstractCsvService } from '#modules/statistics/components/download-insights-modal/models/csv-service.abstract';
import { InsightsByPlatform } from '#modules/statistics/models/insights';
import { StatisticsStore } from '#modules/statistics/stores/store/statistics.store';
import { CSV_NUMBER_LOCALE } from '#shared/helpers/create-csv-string';
import { formatShortNumber } from '#shared/helpers/format-number';
import { EnumTranslatePipe } from '#shared/pipes/enum-translate.pipe';

type Data = InsightsByPlatform | undefined;

@Injectable()
export class PlatformsRatingsCsvInsightsService extends AbstractCsvService<Data> {
    private readonly _statisticsStore = inject(StatisticsStore);
    private readonly _enumTranslatePipe = inject(EnumTranslatePipe);

    protected override _isDataValid(data: Data): boolean {
        return !!data;
    }

    protected override _getData$(): Observable<Data> {
        return of(this._statisticsStore.platformsRatingsData());
    }

    protected override _getCsvDataRows(data: Exclude<Data, undefined>): CsvAsStringArrays {
        return Object.entries(data)
            .map(
                ([platformKey, platformRatings]) =>
                    platformRatings[AggregationTimeScale.BY_DAY]?.platform_rating?.map((rating) => {
                        const date = new Date(rating.date).toISOString();
                        const ratingValue = formatShortNumber(rating.value, CSV_NUMBER_LOCALE);
                        const platformKeyTranslated = this._enumTranslatePipe.transform(platformKey, 'platform_key');
                        return [date, ratingValue, platformKeyTranslated];
                    }) ?? []
            )
            .flat();
    }

    protected override _getCsvHeaderRow(): CsvAsStringArrays[0] {
        return ['Date', 'Rating', 'Platform'];
    }
}
